import React from 'react';
import { useParams } from 'react-router-dom';
import { Backdrop, CircularProgress } from '@mui/material';
import { useGet } from '../lib/api/hooks';
import { CaseDetailsModel } from '../lib/models/CaseDetailsModel';
import 'moment-timezone';
import CaseDetailsHeader from '../components/CaseDetails/CaseDetailsHeader';
import CaseDetailsGeneralInfo from '../components/CaseDetails/CaseDetailsGeneralInfo';
import ClinicalReviewActivityListing from '../components/CaseDetails/ClinicalActivity/ClinicalReviewActivityListing';
import { CaseProcedureModel } from '../lib/models/CaseProcedureModel';
import { ClinicalReviewActivityModel } from '../lib/models/ClinicalReviewActivityModel';
import { PhysicianReviewModel } from '../lib/models/PhysicianReviewModel';
import CaseMessages from '../components/CaseDetails/CaseMessages/CaseMessages';
import BlueRibbon from '../components/CaseDetails/BlueRibbon';
import { CaseStages } from '../lib/constants/misc';
import useTrackPageView from '../lib/hooks/use-track-page-view';
import { backdropStyle } from '../style';
import { DecisionReasonModel } from '../lib/models/DecisionReasonModel';

function CompletedCaseDetailsPage() {
    const trackPageView = useTrackPageView();
    const { id, crmClient } = useParams();
    const { data, loading } = useGet<CaseDetailsModel>(
        `casedetails\\getcasedetails?caseId=${id}&crmId=${crmClient}&isAuditable=${false}`
    );
    const [, setProcedures] = React.useState<CaseProcedureModel[] | undefined>(data?.procedures);
    const [clinicalReviews, setClinicalReviews] = React.useState<
        ClinicalReviewActivityModel[] | undefined
    >(data?.clinicalReviews);
    const [, setPhysicianReviewComment] = React.useState<PhysicianReviewModel | undefined>(
        data?.physicianReviewComment
    );

    const [currentCode] = React.useState<string>('');

    const [selectedReason] = React.useState<DecisionReasonModel[]>([]);

    const [appealedCodeNumber, setAppealedCodeNumber] = React.useState(0);

    React.useEffect(() => {
        return () => {
            if (data && data.caseNumber) {
                trackPageView('Case Details Page (From I&P)', {
                    caseNumber: data.caseNumber,
                    crmClientName: data.crmClientName,
                    caseStage: data.caseStage
                });
            }
        };
    }, [data, trackPageView]);

    React.useEffect(() => {
        setClinicalReviews(data?.clinicalReviews);
    }, [loading, data?.clinicalReviews]);

    React.useEffect(() => {
        setProcedures(data?.procedures);
    }, [loading, selectedReason, currentCode, data?.procedures]);

    React.useEffect(() => {
        setPhysicianReviewComment(data?.physicianReviewComment);
    }, [loading, data?.physicianReviewComment]);

    React.useEffect(() => {
        let count = 0;
        // eslint-disable-next-line
        data?.procedures.map((item) => {
            if (!item.isApproved) {
                count++;
            }
        });
        setAppealedCodeNumber(count);
    }, [loading, data?.procedures]);

    return (
        <>
            {!loading ? (
                <>
                    <CaseDetailsHeader
                        caseNumber={data?.caseNumber}
                        displayDueDate={data?.displayDueDate}
                        displayDueTime={data?.displayDueTime}
                        id={data?.crmCaseId}
                        crmClient={crmClient}
                        clinicalReviewId={id}
                    />
                    {data != null ? (
                        <>
                            {data.caseStage !== CaseStages.Initial &&
                                data.caseStage !== CaseStages.ClaimMedicalReview && (
                                    <BlueRibbon
                                        title={data.caseStage}
                                        appealedCodeNumber={appealedCodeNumber}
                                    />
                                )}

                            <CaseDetailsGeneralInfo
                                data={data}
                                isReadOnly={true}
                                isDialogOpen={false}
                            />
                            <ClinicalReviewActivityListing clinicalReviews={clinicalReviews} />

                            <CaseMessages
                                caseId={data.crmCaseId}
                                crmClient={data.crmClientName}
                                userId={data.userId}
                                caseStage={data.caseStage}
                                isReadonly
                            />
                        </>
                    ) : null}
                </>
            ) : (
                <>
                    <Backdrop sx={backdropStyle} open={loading}>
                        <CircularProgress color="inherit" />
                    </Backdrop>
                </>
            )}
        </>
    );
}

export default CompletedCaseDetailsPage;
